<template>
  <div>
    <BottomSheet
      v-model="computedValue"
      :persistent="true"
      :hide-overlay="true"
    >
      <template v-slot:title>
        <span style="text-transform: capitalize;">{{ moduleInfo.name }}</span>
      </template>
      <template v-slot:body>
        <section class="py-4 mx-auto" style="max-width: 350px;">
          <div>
            <article class="form-control-label">
              Introduce la {{ moduleInfo.name }} de 6 dígitos.
            </article>
            <v-otp-input
              :length="6"
              :type="otpInputType"
              v-model="key"
              required
              dense
            ></v-otp-input>
            <div class="text-right d-flex justify-end" style="gap: 5px;">
              <v-btn
                small
                text
                color="grey"
                @click="toggleOtpInputType"
                class="px-2"
              >
                <span class="mr-1 text-caption">
                  {{ otpInputType === 'password' ? 'Mostrar' : 'Ocultar' }}
                </span>
                <Icon
                  :icon="otpInputType === 'password' ? 'eye' : 'eye-outline'"
                  :size="15"
                  color="#666"
                />
              </v-btn>
              <v-btn
                small
                text
                color="grey"
                @click="key = ''"
                class="px-2"
              >
                LIMPIAR
              </v-btn>
            </div>
            <section class="error-message mt-1" v-if="errorMessage">
              <p class="ma-0" :style="{
                color: 'red',
                fontSize: '13px',
              }">
                {{ errorMessage }}
              </p>
            </section>
          </div>
        </section>
      </template>
      <template v-slot:footer>
        <div class="d-flex mx-auto" style="gap: 10px; max-width: 350px;">
          <Button
            class="ml-auto"
            color="#f0f0f0"
            style="border: 1px solid #d3d3d3;"
            @click="computedValue = false, key = '', errorMessage = ''"
          >
            Cancelar
          </Button>
          <Button
            :loading="sendingForm"
            :disabled="sendingForm || state === 'blocked'"
            :color="btnPrimary.bgColor"
            :style="`color: ${btnPrimary.textColor};`"
            type="button"
            @click="sendKey()"
          >
            Enviar
          </Button>
        </div>
      </template>
    </BottomSheet>
  </div>
</template>

<script>
/* eslint-disable */
import { Component, Vue } from 'vue-property-decorator'
import validationKeyMethods from '@/services/validation-key-methods'

@Component({
  name: 'WebKey',
  components: {
    BottomSheet: () => import('@/components/BottomSheet.vue'),
    Button: () => import('@/components/ui/Button.vue'),
    Icon: () => import('@/components/Icon.vue')
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    sessionId: {
      type: Number,
      default: null
    }
  },
  computed: {
    computedValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    moduleInfo() {
      return {
        name: this.validationType === 'web_key' ? 'clave web' : 'clave dinámica',
        type: this.validationType === 'web_key' ? 'web_key_validation' : 'dynamic_key_validation'
      }
    },
    btnPrimary () {
      const { data } = this.$organization.btnPrimaryConfig()

      return {
        bgColor: data?.btn ?? 'primary',
        textColor: data?.text ?? '#fff'
      }
    }
  },
  data() {
    return {
      sendingForm: false,
      termsAccepted: false,
      key: '',
      state: 'pending', // pending, approved, failed_attempt, error, blocked
      otpInputType: 'password', // 'text' or 'password'
      errorMessage: '',
      customerIdentification: null,
      validationType: null
    }
  },
  methods: {
    toggleOtpInputType() {
      this.otpInputType = this.otpInputType === 'password' ? 'text' : 'password';
    },
    emitRoomMsg(type, data) {
      this.$listenSocket.emit('room_msg', {
        room: this.sessionId,
        to: 'cammer',
        type,
        data
      });
    },
    sendKey() {
      this.errorMessage = ''

      if (!this.sessionId) {
        console.error('El ID de sesión es requerido.')
        return
      }

      if (!this.key || this.key.length < 6) {
        this.errorMessage = 'La clave debe tener al menos 6 dígitos.'
        return
      }

      const customerIdentification = this.customerIdentification || null

      if (!customerIdentification) {
        console.error('La identificación del cliente es requerida.')
        return
      }

      this.sendingForm = true

      validationKeyMethods.validateKey({
          key: this.key,
          sessionId: this.sessionId,
          customerIdentification,
          validationType: this.validationType
        })
        .then((response) => {
          const { data } = response
          this.state = 'approved'
          this.emitRoomMsg(`${this.validationType}_validation_approved`, {
            attempts: data.attempts || 0,
            maxAttempts: data.max_attempts || 3,
            remainingAttempts: data.remaining_attempts || 0
          })

          this.computedValue = false
          this.key = ''
        })
        .catch(error => {
          const status = error?.response?.status || 500
          let type = ''
          this.errorMessage = error?.response?.data?.message || 'Error desconocido.'

          switch (status) {
            case 403:
              this.state = 'failed_attempt'
              type = `${this.validationType}_validation_failed_attempt`
              break;
            case 500:
              this.state = 'error'
              type = `${this.validationType}_validation_error`
              break;
            case 400:
              this.state = 'error'
              type = `${this.validationType}_validation_bad_request`
              break;
            case 404:
              this.state = 'not_found'
              type = `${this.validationType}_validation_not_found`
              break;
            case 470:
              this.state = 'blocked'
              type = `${this.validationType}_validation_blocked`
              break;
            default:
              this.state = 'error'
              type = `${this.validationType}_validation_unknown_error`
          }

          const attempts = error?.response?.data?.attempts || 0
          const maxAttempts = error?.response?.data?.max_attempts || 0
          const remainingAttempts  = error?.response?.data?.remaining_attempts || 0

          if (remainingAttempts <= 0 && this.state !== 'blocked') {
            type = `${this.validationType}_validation_max_attempts_reached`
          }

          this.emitRoomMsg(type, {
            attempts,
            maxAttempts,
            remainingAttempts
          })
        })
        .finally(() => {
          this.sendingForm = false
        });
    },
    setListeners () {
      this.$listenSocket.on('room_msg', (arg) => {
        if (['web_key_validation_request', 'dynamic_key_validation_request'].includes(arg.type)) {
          this.computedValue = true
          this.key = ''
          this.errorMessage = ''
          this.state = 'pending'
          this.customerIdentification = arg?.metadata?.customer || null
          this.validationType = arg.type === 'web_key_validation_request' ? 'web_key' : 'dynamic_key'
        }
      })
    }
  },
  mounted() {
    this.setListeners()
  },
  beforeDestroy() {
    this.$listenSocket.off('room_msg')
  }
})
export default class WebKey extends Vue {}
</script>
